import { useCallback, useEffect, useRef, useState } from "react";

const TWENTY_FOUR_HOURS = 24 * 3600; // 24小时 = 86400秒
const SEVENTY_TWO_HOURS = 72 * 3600; // 72小时 = 259200秒
export default function useCountdown(countdown: number) {
  const [timeLeft, setTimeLeft] = useState<number>(countdown);
  const [isExpired, setIsExpired] = useState<boolean>(countdown <= 0);
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 保存初始 countdown，用于计算过期日期
  const initialCountdownRef = useRef<number>(countdown);
  const expireDateRef = useRef<Date | null>(null);

  // 启动每秒更新的定时器
  const startIntervalTimer = () => {
    // 如果已经有定时器在运行，先清除
    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
    }

    intervalTimerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (intervalTimerRef.current) {
            clearInterval(intervalTimerRef.current);
            intervalTimerRef.current = null;
          }
          // 倒计时结束，设置过期状态
          setIsExpired(true);
          return 0;
        }
        return next;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!countdown || countdown <= 0) return;

    // countdown 是剩余秒数，直接使用
    setTimeLeft(countdown);

    // 保存初始countdown并计算过期日期
    initialCountdownRef.current = countdown;
    expireDateRef.current = new Date(Date.now() + countdown * 1000);

    // 计算当前时间距离状态切换点还有多久
    let switchDelay: number | null = null;
    let targetTime: number | null = null;

    if (countdown >= SEVENTY_TWO_HOURS) {
      // 剩余时间在72h以上，计算距离72h还有多久
      switchDelay = (countdown - SEVENTY_TWO_HOURS) * 1000; // 转换为毫秒
      targetTime = SEVENTY_TWO_HOURS;
    } else if (countdown >= TWENTY_FOUR_HOURS) {
      // 剩余时间在24h以上、72h以内，计算距离24h还有多久
      switchDelay = (countdown - TWENTY_FOUR_HOURS) * 1000; // 转换为毫秒
      targetTime = TWENTY_FOUR_HOURS;
    } else {
      // 剩余时间小于24h，直接开始每秒更新
      startIntervalTimer();
      return () => {
        if (intervalTimerRef.current) {
          clearInterval(intervalTimerRef.current);
          intervalTimerRef.current = null;
        }
      };
    }

    // 如果需要在某个时间点切换状态
    if (switchDelay !== null && switchDelay >= 0 && targetTime !== null) {
      const switchTimer = setTimeout(() => {
        // 到达切换点，更新为切换后的时间值，然后开始每秒更新
        setTimeLeft(targetTime!);
        startIntervalTimer();
      }, switchDelay);

      // 清理函数
      return () => {
        clearTimeout(switchTimer);
        if (intervalTimerRef.current) {
          clearInterval(intervalTimerRef.current);
          intervalTimerRef.current = null;
        }
      };
    }
  }, [countdown]);

  const formatTime = useCallback((seconds: number) => {
    const totalHours = seconds / 3600;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // 小于24小时，显示倒计时
    if (totalHours < 24) {
      if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
          secs
        ).padStart(2, "0")}`;
      }
      return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }

    // 大于24小时小于72小时，显示x天过期
    if (totalHours >= 24 && totalHours < 72) {
      const days = Math.floor(totalHours / 24);
      return `${days}天过期`;
    }

    // 大于等于72小时，显示具体过期日期
    if (expireDateRef.current) {
      const expireDate = expireDateRef.current;
      const month = expireDate.getMonth() + 1; // getMonth() 返回 0-11
      const day = expireDate.getDate();
      return `${month}月${day}日过期`;
    }

    // 兜底：如果无法计算过期日期，显示天数
    const days = Math.floor(totalHours / 24);
    return `${days}天过期`;
  }, []);

  return {
    countdownText: formatTime(timeLeft),
    isExpired,
  };
}
